import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, ListTodo, CheckCircle2, RefreshCw } from 'lucide-react';
import config from '../config';

const API = `${config.PASSPORT_API_URL}/api/work-lists`;

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.UserId || null;
  } catch {
    return null;
  }
};

const getPermissions = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return JSON.parse(user?.Permissions || '{}') || {};
  } catch {
    return {};
  }
};

const colors = ['#2563EB','#059669','#7C3AED','#EA580C','#DC2626','#0891B2','#475569'];

const WorkListPickerModal = ({ open, onClose, items = [], onDone, title = 'إضافة إلى قائمة عمل' }) => {
  const permissions = getPermissions();
  const actionPerms = permissions['إدارة قوائم العمل'];
  const can = (name) => !actionPerms || !Object.keys(actionPerms).length || !Object.prototype.hasOwnProperty.call(actionPerms,name) ? true : actionPerms[name] === true;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#2563EB');
  const [error, setError] = useState('');

  const uniqueItems = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(items) ? items : []).filter(item => {
      const type = String(item?.SourceType || '').toUpperCase();
      const id = Number(item?.SourceID);
      if (!['TEST','PASSPORT'].includes(type) || !Number.isInteger(id) || id <= 0) return false;
      const key = `${type}:${id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [items]);

  const loadLists = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API}?status=open`);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'تعذر جلب قوائم العمل');
      const rows = Array.isArray(data?.data) ? data.data : [];
      setLists(rows);
      if (!selectedListId && rows.length) setSelectedListId(String(rows[0].WorkListID));
    } catch (e) {
      setError(e.message || 'تعذر جلب قوائم العمل');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedListId('');
    setShowCreate(false);
    setNewName('');
    setError('');
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const createList = async () => {
    if (!can('إنشاء قائمة')) { setError('ليس لديك صلاحية إنشاء قائمة عمل'); return; }
    const name = newName.trim();
    if (!name) {
      setError('اكتب اسم قائمة العمل');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const response = await fetch(API, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ListName:name, ColorHex:newColor, CreatedByUserID:getUserId()})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'تعذر إنشاء القائمة');
      const created = data?.data;
      await loadLists();
      if (created?.WorkListID) setSelectedListId(String(created.WorkListID));
      setShowCreate(false);
      setNewName('');
    } catch (e) {
      setError(e.message || 'تعذر إنشاء القائمة');
    } finally {
      setSaving(false);
    }
  };

  const addItems = async () => {
    if (!can('إضافة أشخاص')) { setError('ليس لديك صلاحية إضافة أشخاص إلى قوائم العمل'); return; }
    if (!selectedListId) {
      setError('اختر قائمة عمل أولاً');
      return;
    }
    if (!uniqueItems.length) {
      setError('لا يوجد أشخاص صالحون للإضافة');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const response = await fetch(`${API}/${selectedListId}/items`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          items: uniqueItems.map(({SourceType,SourceID}) => ({SourceType,SourceID})),
          AddedByUserID:getUserId()
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'تعذر إضافة الأشخاص');
      onDone?.(data);
      onClose?.();
    } catch (e) {
      setError(e.message || 'تعذر إضافة الأشخاص');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1400] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b bg-slate-50 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-black text-lg text-slate-900 flex items-center gap-2"><ListTodo size={20}/>{title}</h2>
            <p className="text-xs text-slate-500 mt-1">سيتم إضافة {uniqueItems.length.toLocaleString('ar-EG')} شخص بدون نسخ بياناتهم الأصلية.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-200"><X size={20}/></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 font-bold text-sm">{error}</div>}

          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-black text-slate-800">اختر قائمة العمل</h3>
            <div className="flex gap-2">
              <button onClick={loadLists} disabled={loading} className="p-2 rounded-xl border hover:bg-slate-50 text-slate-600" title="تحديث">
                <RefreshCw size={17} className={loading?'animate-spin':''}/>
              </button>
              {can('إنشاء قائمة') && <button onClick={()=>setShowCreate(v=>!v)} className="px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold inline-flex items-center gap-2">
                <Plus size={17}/> قائمة جديدة
              </button>}
            </div>
          </div>

          {showCreate && (
            <div className="mb-4 p-4 rounded-2xl bg-blue-50 border border-blue-100">
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">اسم القائمة</label>
                  <input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();createList();}}} autoFocus placeholder="مثال: مراجعة رخص ركاب" className="w-full px-3 py-2.5 rounded-xl border bg-white focus:ring-2 focus:ring-blue-100 outline-none"/>
                </div>
                <button onClick={createList} disabled={saving} className="px-4 py-2.5 rounded-xl bg-slate-900 text-white font-bold">إنشاء</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {colors.map(color=><button key={color} type="button" onClick={()=>setNewColor(color)} className={`w-8 h-8 rounded-full border-4 ${newColor===color?'border-slate-900':'border-white'}`} style={{background:color}} title={color}/>) }
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-10 text-center text-slate-500"><RefreshCw className="animate-spin mx-auto mb-2"/>جاري تحميل القوائم...</div>
          ) : lists.length ? (
            <div className="space-y-2">
              {lists.map(list => {
                const selected = String(list.WorkListID) === String(selectedListId);
                const total = Number(list.TotalItems || 0);
                const done = Number(list.DoneItems || 0);
                return (
                  <button key={list.WorkListID} type="button" onClick={()=>setSelectedListId(String(list.WorkListID))} className={`w-full text-right p-4 rounded-2xl border-2 transition flex items-center gap-3 ${selected?'border-blue-500 bg-blue-50':'border-slate-200 hover:bg-slate-50'}`}>
                    <span className="w-3 h-10 rounded-full shrink-0" style={{background:list.ColorHex || '#2563EB'}}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-900 truncate">{list.ListName}</div>
                      <div className="text-xs text-slate-500 mt-1">{total.toLocaleString('ar-EG')} شخص • {done.toLocaleString('ar-EG')} تم</div>
                    </div>
                    {selected && <CheckCircle2 className="text-blue-600 shrink-0" size={22}/>} 
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10 border-2 border-dashed rounded-2xl text-slate-500">لا توجد قوائم مفتوحة. أنشئ قائمة جديدة أولاً.</div>
          )}
        </div>

        <div className="px-5 py-4 border-t bg-slate-50 flex items-center justify-between gap-3">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border bg-white font-bold text-slate-700">إلغاء</button>
          <button onClick={addItems} disabled={saving || !can('إضافة أشخاص') || !selectedListId || !uniqueItems.length} className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black disabled:opacity-50 inline-flex items-center gap-2">
            {saving?<RefreshCw size={17} className="animate-spin"/>:<Plus size={17}/>} إضافة {uniqueItems.length.toLocaleString('ar-EG')} شخص
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkListPickerModal;
